import { GlobalMenuGroupsRouteNames } from '@centric-os/global-menu-groups';
import { LocalMenuGroupRouteNames } from '@centric-os/local-menu-groups';
import { StockRouteNames } from '@centric-os/stock';
import { SitesRouteNames } from '@centric-os/sites';
import { OrdersRouteNames } from '@centric-os/orders';
import { defaultOrderPaginationConfig } from '@centric-os/stores/orders/use-order-store';
import type { NavItem } from '@centric-os/stores/navigation/use-main-navigation-store';
import { ScannerRouteNames } from '@centric-os/scanner';
import { LibrariesRouteNames } from '@centric-os/libraries';
import { ForecastingRouteNames } from '@centric-os/forecasting';

import {
  OrderPaginationQueryKeys,
  defaultBrandNavigationPaginationConfig,
  defaultMenusNavigationPaginationConfig,
  defaultSitePaginationConfig,
  announcementsListPaginationConfig,
  discountsListPaginationConfig,
} from '@centric-os/types';
import { ReportsRouteNames } from '../../../app-modules/reports';
import { AP3UserManagementRouteNames } from '../../../ap3/user-management';
import { AnnouncementsRouteNames } from '@centric-os/announcements';
import { DiscountsRouteNames } from '@centric-os/discounts';
import { LoyaltyRouteNames } from '@centric-os/loyalty/src/enums/router-names';
import { LoginRouteNames } from '@/views/app-modules/login';
import { InventoryRouteNames } from '@/views/app-modules/inventory';
import { LabsRouteNames } from '@/views/app-modules/labs';
import { AgentRouteNames } from '@/views/app-modules/agent';
import { DiscoverRouteNames } from '@/views/app-modules/discover';
import {
  House,
  ShoppingCart,
  BookOpen,
  Archive,
  ChartNoAxesColumn,
  Volume2,
  Tag,
  Heart,
  Binoculars,
  Compass,
  Sparkles,
  Users,
  ChartPie,
  LogOut,
} from 'lucide-vue-next';
// DOC for reference https://github.com/yaminncco/vue-sidebar-menu

type Translate = (key: string) => string;

export const buildNavigationConfig = (t: Translate): NavItem[] => [
  {
    key: 'sites.root',
    title: t('navigation.sites.root'),
    icon: { element: House },
    href: {
      name: SitesRouteNames.SITES_LIST,
      query: {
        limit: defaultSitePaginationConfig.queryParams.limit,
        page: defaultSitePaginationConfig.queryParams.page,
        sortBy: defaultSitePaginationConfig.queryParams.sortBy,
        sortOrder: defaultSitePaginationConfig.queryParams.sortOrder,
      },
    },
    disabled: false,
  },
  {
    key: 'orders.root',
    title: t('navigation.orders.root'),
    icon: { element: ShoppingCart },
    href: {
      name: OrdersRouteNames.ORDER_LIST,
      query: {
        [OrderPaginationQueryKeys.LIMIT]:
          defaultOrderPaginationConfig.queryParams[OrderPaginationQueryKeys.LIMIT],
        [OrderPaginationQueryKeys.PAGE]:
          defaultOrderPaginationConfig.queryParams[OrderPaginationQueryKeys.PAGE],
        [OrderPaginationQueryKeys.SORT_BY]:
          defaultOrderPaginationConfig.queryParams[OrderPaginationQueryKeys.SORT_BY],
        [OrderPaginationQueryKeys.SORT_ORDER]:
          defaultOrderPaginationConfig.queryParams[OrderPaginationQueryKeys.SORT_ORDER],
        [OrderPaginationQueryKeys.DATE_CREATED_START]:
          defaultOrderPaginationConfig.queryParams[OrderPaginationQueryKeys.DATE_CREATED_START],
        [OrderPaginationQueryKeys.DATE_CREATED_END]:
          defaultOrderPaginationConfig.queryParams[OrderPaginationQueryKeys.DATE_CREATED_END],
      },
    },
    disabled: false,
  },
  {
    key: 'menus.root',
    title: t('navigation.menus.root'),
    icon: { element: BookOpen },
    disabled: false,
    isRoot: true,
    child: [
      {
        key: 'menus.global',
        title: t('navigation.menus.global'),
        href: {
          name: GlobalMenuGroupsRouteNames.GLOBAL_MENU_GROUPS_LIST,
          query: {
            limit: defaultBrandNavigationPaginationConfig.queryParams.limit,
            page: defaultBrandNavigationPaginationConfig.queryParams.page,
            sortBy: defaultBrandNavigationPaginationConfig.queryParams.sortBy,
            sortOrder: defaultBrandNavigationPaginationConfig.queryParams.sortOrder,
          },
        },
        icon: 'fake-icon',
        disabled: true,
      },
      {
        key: 'menus.local',
        title: t('navigation.menus.local'),
        href: {
          name: LocalMenuGroupRouteNames.LOCAL_MENUS_LIST,
          query: {
            limit: defaultMenusNavigationPaginationConfig.queryParams.limit,
            page: defaultMenusNavigationPaginationConfig.queryParams.page,
            sortBy: defaultMenusNavigationPaginationConfig.queryParams.sortBy,
            sortOrder: defaultMenusNavigationPaginationConfig.queryParams.sortOrder,
          },
        },
        icon: 'fake-icon',
        disabled: true,
      },
      {
        key: 'menus.stock',
        title: t('navigation.menus.stock'),
        href: {
          name: StockRouteNames.STOCK_SITES,
        },
        icon: 'fake-icon',
        disabled: true,
      },
    ],
  },
  {
    key: 'libraries.root',
    title: t('navigation.libraries.root'),
    icon: { element: Archive },
    disabled: false,
    isRoot: true,
    href: {
      name: LibrariesRouteNames.LIBRARIES_LIST,
    },
  },
  {
    key: 'reports.root',
    title: t('navigation.reports.root'),
    icon: { element: ChartNoAxesColumn },
    disabled: false,
    isRoot: true,
    child: [
      {
        key: 'reports.salesReading',
        title: t('navigation.reports.salesReading'),
        href: {
          name: ReportsRouteNames.SALES_READING,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.salesSummary',
        title: t('navigation.reports.salesSummary'),
        href: {
          name: ReportsRouteNames.SALES_SUMMARY,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.salesDistribution',
        title: t('navigation.reports.salesDistribution'),
        href: {
          name: ReportsRouteNames.SALES_DISTRIBUTION,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.salesMix',
        title: t('navigation.reports.salesMix'),
        href: {
          name: ReportsRouteNames.SALES_MIX,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.refundReport',
        title: t('navigation.reports.refundReport'),
        href: {
          name: ReportsRouteNames.REFUND,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.terminalSales',
        title: t('navigation.reports.terminalSales'),
        href: {
          name: ReportsRouteNames.TERMINAL_SALES,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.discounts',
        title: t('navigation.reports.discounts'),
        href: {
          name: ReportsRouteNames.DISCOUNTS,
        },
        disabled: false,
        icon: 'fake-icon',
      },
      {
        key: 'reports.reportingDiagnostics',
        title: t('navigation.reports.reportingDiagnostics'),
        href: {
          name: ReportsRouteNames.REPORTING_DIAGNOSTICS,
        },
        disabled: false,
        icon: 'fake-icon',
      },
    ],
  },
  {
    key: 'announcements.root',
    title: t('navigation.announcements.root'),
    icon: { element: Volume2 },
    href: {
      name: AnnouncementsRouteNames.ANNOUNCEMENTS_LIST,
      query: {
        status: announcementsListPaginationConfig?.queryParams?.status,
        end_date_start: announcementsListPaginationConfig?.queryParams?.end_date_start,
        end_date_end: announcementsListPaginationConfig?.queryParams?.end_date_end,
        limit: announcementsListPaginationConfig.queryParams.limit,
        page: announcementsListPaginationConfig.queryParams.page,
        sortBy: announcementsListPaginationConfig.queryParams.sortBy,
        sortOrder: announcementsListPaginationConfig.queryParams.sortOrder,
      },
    },
    disabled: false,
  },
  {
    key: 'discounts.root',
    title: t('navigation.discounts.root'),
    icon: { element: Tag },
    disabled: false,
    isRoot: true,
    child: [
      {
        key: 'discounts.centric',
        title: t('navigation.discounts.centric'),
        href: {
          name: DiscountsRouteNames.DISCOUNTS_LIST,
          query: {
            end_date_start: discountsListPaginationConfig?.queryParams?.end_date_start,
            end_date_end: discountsListPaginationConfig?.queryParams?.end_date_end,
            limit: discountsListPaginationConfig.queryParams.limit,
            page: discountsListPaginationConfig.queryParams.page,
            sortBy: discountsListPaginationConfig.queryParams.sortBy,
            sortOrder: discountsListPaginationConfig.queryParams.sortOrder,
          },
        },
        exact: true,
        icon: 'fake-icon',
        disabled: false,
      },
      {
        key: 'discounts.ryst',
        title: t('navigation.discounts.ryst'),
        href: {
          name: DiscountsRouteNames.RYST_PROMO_MANAGEMENT,
        },
        icon: 'fake-icon',
        disabled: false,
        badge: {
          text: 'beta',
          class: 'vsm--badge_default text-xxs bg-[var(--blue-100)] mr-2',
        },
      },
    ],
  },
  {
    key: 'loyalty.root',
    title: t('navigation.loyalty.root'),
    icon: { element: Heart },
    disabled: false,
    isRoot: true,
    child: [
      {
        key: 'loyalty.campaigns',
        title: t('navigation.loyalty.campaigns'),
        href: {
          name: LoyaltyRouteNames.CAMPAIGNS_LIST,
        },
        icon: 'fake-icon',
        disabled: false,
        badge: {
          text: 'beta',
          class: 'vsm--badge_default text-xxs bg-[var(--blue-100)] mr-2',
        },
      },
      {
        key: 'loyalty.vouchers',
        title: t('navigation.loyalty.vouchers'),
        href: {
          name: LoyaltyRouteNames.VOUCHERS_LIST,
        },
        icon: 'fake-icon',
        disabled: false,
        badge: {
          text: 'beta',
          class: 'vsm--badge_default text-xxs bg-[var(--blue-100)] mr-2',
        },
      },
      {
        key: 'loyalty.rewards',
        title: t('navigation.loyalty.rewards'),
        href: {
          name: LoyaltyRouteNames.REWARDS_LIST,
        },
        icon: 'fake-icon',
        disabled: false,
        badge: {
          text: 'beta',
          class: 'vsm--badge_default text-xxs bg-[var(--blue-100)] mr-2',
        },
      },
      {
        key: 'loyalty.segments',
        title: t('navigation.loyalty.segments'),
        href: {
          name: LoyaltyRouteNames.SEGMENTS_LIST,
        },
        icon: 'fake-icon',
        disabled: false,
        badge: {
          text: 'beta',
          class: 'vsm--badge_default text-xxs bg-[var(--blue-100)] mr-2',
        },
      },
      {
        key: 'loyalty.bundles',
        title: t('navigation.loyalty.bundles'),
        href: {
          name: LoyaltyRouteNames.BUNDLES_LIST,
        },
        icon: 'fake-icon',
        disabled: false,
        badge: {
          text: 'beta',
          class: 'vsm--badge_default text-xxs bg-[var(--blue-100)] mr-2',
        },
      },
      // Loyalty reporting is intentionally unlinked: the two report views are
      // not built out (they aggregate client-side and silently truncate past
      // the first page), so they stay out of the menu rather than shipping
      // numbers an operator would trust. The routes stay registered so the
      // work remains reachable by URL while it is finished.
    ],
  },
  {
    key: 'forecasting.root',
    title: t('navigation.forecasting.root'),
    icon: { element: Binoculars },
    disabled: false,
    isRoot: true,
    href: {
      name: ForecastingRouteNames.FORECASTING_SHEET,
    },
    badge: {
      text: 'beta',
      class: 'vsm--badge_default text-xxs bg-[var(--blue-100)]',
    },
  },
  {
    key: 'discover.root',
    title: t('navigation.discover.root'),
    icon: { element: Compass },
    disabled: false,
    isRoot: true,
    href: {
      name: DiscoverRouteNames.DISCOVER_LIST,
    },
  },
  {
    key: 'inventory.root',
    title: t('navigation.inventory.root'),
    icon: {
      element: 'i',
      class: 'material-symbols-rounded text-[1.25rem]',
      text: 'inventory',
    },
    disabled: false,
    isRoot: true,
    child: [
      {
        key: 'inventory.desktop',
        title: t('navigation.inventory.desktop'),
        href: {
          name: InventoryRouteNames.INVENTORY_ROOT,
        },
        icon: 'fake-icon',
        disabled: false,
      },
      {
        key: 'inventory.mobile',
        title: t('navigation.inventory.mobile'),
        href: 'https://apps.powerapps.com/play/e/d8f7c061-6444-e5c9-bb46-6b4cf957d5f4/a/62dcf21c-fdbc-47a2-8745-e44d6a64d14d?tenantId=cd62b7dd-4b48-44bd-90e7-e143a22c8ead&hint=02ad5625-7fe9-4641-9347-cecfa7713451&source=sharebutton&sourcetime=1746796964848&hidenavbar=true',
        external: true,
        icon: 'fake-icon',
        disabled: false,
      },
    ],
  },
  {
    key: 'labs.root',
    title: t('navigation.labs.root'),
    icon: {
      element: 'i',
      class: 'material-symbols-rounded text-[1.25rem]',
      text: 'labs',
    },
    href: {
      name: LabsRouteNames.LABS,
    },
    disabled: false,
    isRoot: true,
  },
  {
    key: 'agent.root',
    title: t('navigation.agent.root'),
    icon: { element: Sparkles },
    href: {
      name: AgentRouteNames.AGENT,
    },
    disabled: false,
    isRoot: true,
    badge: {
      text: 'beta',
      class: 'vsm--badge_default text-xxs bg-[var(--blue-100)]',
    },
  },
  {
    key: 'adminPanel.nav',
    header: t('navigation.adminPanel.nav'),
    isHeader: true,
    hiddenOnCollapse: true,
  },
  {
    key: 'adminPanel.userManagement.root',
    title: t('navigation.adminPanel.userManagement.root'),
    icon: { element: Users },
    href: {
      name: AP3UserManagementRouteNames.AP3_USERS_LIST,
    },
    disabled: false,
  },
  {
    key: 'external',
    header: t('navigation.external'),
    isHeader: true,
    hiddenOnCollapse: true,
  },
  {
    key: 'operatorAnalytics.root',
    title: t('navigation.operatorAnalytics.root'),
    icon: { element: ChartPie },
    href: import.meta.env.VITE_OPERATOR_ANALYTICS_URL,
    external: true,
    disabled: false,
    badge: {
      text: 'sso req',
      class: 'vsm--badge_default text-xxs',
    },
  },
  {
    key: 'apps',
    header: t('navigation.apps'),
    isHeader: true,
    hiddenOnCollapse: true,
  },
  {
    key: 'scanner.root',
    title: t('navigation.scanner.root'),
    icon: {
      element: 'i',
      class: 'material-symbols-rounded text-[1.25rem]',
      // attributes: {}
      text: 'barcode_scanner',
    },
    href: {
      name: ScannerRouteNames.SCANNER_ROOT,
    },
    disabled: false,
    badge: {
      text: 'new',
      class: 'vsm--badge_default text-xxs bg-[var(--green-100)]',
    },
  },
  {
    key: 'user',
    header: 'User',
    title: t('navigation.user'),
    isHeader: true,
    hiddenOnCollapse: true,
  },
  {
    key: 'logout.root',
    title: t('navigation.logout.root'),
    icon: { element: LogOut },
    href: {
      name: LoginRouteNames.LOGIN,
    },
    disabled: false,
  },
];
